import { useSelector } from 'react-redux';
import Media from 'react-media';
import loadable from '@loadable/component';

import { useTheme } from '@simplywallst/ui-core';
import type { Theme } from '@simplywallst/ui-core';

import { getUi } from '@/redux/ui/selectors';

import SwitcherContainer from '@/containers/SwitcherContainer';

import Onboarding from '@/components/Onboarding';
import { BrazeProvider } from '@/components/Braze';
import { LayoutProvider } from '@/components/Layout/context/LayoutContext';

import { useUser } from '@/hooks/useUser';
import { useMobileDetect } from '@/hooks/useMobileDetect';
import { Provider as SearchProvider } from '@/hooks/useSearch';

import { useAppInit } from './hooks/useAppInit';

import { Body } from './styled';
import { MainContainer } from './components/MainContainer';

const SnackBarContainer = loadable(
  () => import('@/components/SnackBar/containers/SnackBarContainer'),
  { ssr: false }
);

const LoadingOverlay = loadable(() => import('@/components/LoadingOverlay'), {
  ssr: false,
});

const MobileFooterMenuContainer = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/components/MobileFooterMenu/containers/MobileFooterMenuContainer'
    )
);
const TrialEndedPlansModal = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/components/OurPlans/components/TrialEndedPlansModal/TrialEndedPlansModal'
    )
);
const CheckoutReturnModal = loadable(
  () => import('@/components/OurPlans/components/CheckoutReturnModal')
);

const IgnoreOnboardingExperienceTest1 = loadable(
  () =>
    import(
      '@/components/Dashboard/components/GettingStartedChecklistExperiment'
    ),
  { ssr: false, resolveComponent: (c) => c.IgnoreOnboardingExperienceTest1 }
);

export interface WithThemeProps {
  theme: Theme;
}

export interface Props {
  hasSnackbarNotification: boolean;
  isAuthenticated: boolean;
  layout: Record<string, any>;
  isRegistrationFromNews: boolean;
}

const Content = ({
  hasSnackbarNotification,
  isAuthenticated,
  layout,
  isRegistrationFromNews,
}: Props) => {
  const theme = useTheme();
  const ui = useSelector(getUi);
  const { data: user } = useUser();
  const isMobile = useMobileDetect();

  const isLoading = ui.loading;

  useAppInit();
  return (
    <MainContainer>
      <BrazeProvider>
        <LayoutProvider layout={layout}>
          <SearchProvider value={null}>
            <Body
              justifyContent="center"
              hasMobileFooterMenu={layout && layout.mobileFooterMenu !== false}
            >
              <SwitcherContainer />
            </Body>
            {layout && layout.mobileFooterMenu !== false && (
              <Media
                query={{ maxWidth: theme.mdMaxRaw }}
                defaultMatches={isMobile}
                render={() => <MobileFooterMenuContainer />}
              />
            )}
            {layout && (
              <>
                {hasSnackbarNotification && <SnackBarContainer />}
                {isLoading && <LoadingOverlay />}
                <TrialEndedPlansModal />
                {isAuthenticated && <CheckoutReturnModal />}
                <Onboarding isRegistrationFromNews={isRegistrationFromNews} />
              </>
            )}
            {user.loggedIn && <IgnoreOnboardingExperienceTest1 user={user} />}
          </SearchProvider>
        </LayoutProvider>
      </BrazeProvider>
    </MainContainer>
  );
};

export default Content;
